/*
 * Modified from https://github.com/backstage/backstage/plugins/catalog/src/components/EntityLayout/EntityLayout.tsx
 *
 * ORIGINAL LICENSE:
 * -- Copyright 2020 The Backstage Authors
 * --
 * -- Licensed under the Apache License, Version 2.0 (the "License");
 * -- you may not use this file except in compliance with the License.
 * -- You may obtain a copy of the License at
 * --
 * --     http://www.apache.org/licenses/LICENSE-2.0
 * --
 * -- Unless required by applicable law or agreed to in writing, software
 * -- distributed under the License is distributed on an "AS IS" BASIS,
 * -- WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * -- See the License for the specific language governing permissions and
 * -- limitations under the License.
 */

import { catalogPlugin } from '@backstage/plugin-catalog';
import {
  Entity,
  DEFAULT_NAMESPACE,
  RELATION_OWNED_BY,
  EntityRelation,
} from '@backstage/catalog-model';
import {
  Breadcrumbs,
  Content,
  Header,
  HeaderLabel,
  Link,
  Page,
  Progress,
  RoutedTabs,
  WarningPanel,
} from '@backstage/core-components';
import {
  attachComponentData,
  createExternalRouteRef,
  IconComponent,
  useApi,
  useElementFilter,
  useRouteRef,
  useRouteRefParams,
} from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityDisplayName,
  EntityRefLink,
  EntityRefLinks,
  entityRouteRef,
  FavoriteEntity,
  getEntityRelations,
  InspectEntityDialog,
  UnregisterEntityDialog,
  useAsyncEntity,
} from '@backstage/plugin-catalog-react';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import { TabProps } from '@material-ui/core/Tab';
import Alert from '@material-ui/lab/Alert';
import {
  ComponentProps,
  ElementType,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useAsync from 'react-use/esm/useAsync';
import { EntityContextMenu } from './EntityContextMenu';

export const rootRouteRef = catalogPlugin.routes.catalogIndex;
export const unregisterRedirectRouteRef = createExternalRouteRef({
  id: 'catalog:unregister-redirect',
  optional: true,
});

/** @public */
export type EntityLayoutRouteProps = {
  path: string;
  title: string;
  children: JSX.Element;
  if?: (entity: Entity) => boolean;
  tabProps?: TabProps<ElementType, { component?: ElementType }>;
};

const dataKey = 'plugin.catalog.entityLayoutRoute';

const Route: (props: EntityLayoutRouteProps) => null = () => null;
attachComponentData(Route, dataKey, true);
attachComponentData(Route, 'core.gatherMountPoints', true); // This causes all mount points that are discovered within this route to use the path of the route itself

function EntityLayoutTitle(
  props: Readonly<{ title: string; entity: Entity | undefined }>,
) {
  const { entity, title } = props;
  return (
    <Box display="inline-flex" alignItems="center" height="1em" maxWidth="100%">
      <Box
        component="span"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        overflow="hidden"
      >
        {entity ? <EntityDisplayName entityRef={entity} hideIcon /> : title}
      </Box>
      {entity && <FavoriteEntity entity={entity} />}
    </Box>
  );
}

function generateHeaderType(entityKind: string, entityType?: string): string {
  const formattedEntityType = entityType ? ` - ${entityType}` : '';

  switch (entityKind.toLowerCase()) {
    case 'api':
      return `API${formattedEntityType}`;
    case 'component':
      return `Component${formattedEntityType}`;
    case 'system':
      return 'Product';
    case 'domain':
      return 'Product Family';
    case 'group':
      if (entityType === 'solution-line') {
        return 'Organization';
      } else if (entityType === 'organization') {
        return 'Company';
      }
      return entityType ?? '';
    default:
      return entityKind;
  }
}

function generateHeaderTitle(name: string, namespace: string): string {
  const formattedNamespace =
    namespace !== DEFAULT_NAMESPACE ? ` in ${namespace}` : '';
  return `${name}${formattedNamespace}`;
}

function headerProps(
  paramKind: string | undefined,
  paramNamespace: string | undefined,
  paramName: string | undefined,
  entity: Entity | undefined,
): { headerTitle: string; headerType: string } {
  const kind = paramKind ?? entity?.kind ?? '';
  const namespace = paramNamespace ?? entity?.metadata.namespace ?? '';
  const name =
    entity?.metadata.title ?? paramName ?? entity?.metadata.name ?? '';
  const entityType = entity?.spec?.type
    ? entity.spec.type.toLocaleString('en-US') // NOSONAR - String will always resolve if entity exists
    : undefined;
  return {
    headerTitle: generateHeaderTitle(name, namespace),
    headerType: generateHeaderType(kind, entityType),
  };
}

function EntityLabels(props: Readonly<{ entity: Entity }>) {
  const { entity } = props;
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
  return (
    <>
      {ownedByRelations.length > 0 && (
        <HeaderLabel
          label="Owner"
          contentTypograpyRootComponent="p"
          value={
            <EntityRefLinks
              entityRefs={ownedByRelations}
              defaultKind="Group"
              color="inherit"
            />
          }
        />
      )}
      {entity.spec?.experience && (
        <HeaderLabel
          label="Experience"
          value={entity.spec.experience as string}
        />
      )}
      {entity.spec?.lifecycle && (
        <HeaderLabel
          label="Lifecycle"
          value={entity.spec.lifecycle as string}
        />
      )}
      {entity.metadata.annotations &&
        'mdsol/audience' in entity.metadata.annotations && (
          <HeaderLabel
            label="Audience"
            value={entity.metadata.annotations['mdsol/audience']}
          />
        )}
    </>
  );
}

// NOTE(freben): Intentionally not exported at this point, since it's part of
// the unstable extra context menu items concept below
interface ExtraContextMenuItem {
  title: string;
  Icon: IconComponent;
  onClick: () => void;
}

type VisibleType = 'visible' | 'hidden' | 'disable';

// NOTE(blam): Intentionally not exported at this point, since it's part of
// unstable context menu option, eg: disable the unregister entity menu
interface EntityContextMenuOptions {
  disableUnregister: boolean | VisibleType;
}

/** @public */
export interface EntityLayoutProps {
  extraContextMenuItems?: ExtraContextMenuItem[];
  contextMenuOptions?: EntityContextMenuOptions;
  children?: ReactNode;
  NotFoundComponent?: ReactNode;
  parentEntityRelations?: string[];
}

function findParentRelation(
  entityRelations: EntityRelation[] = [],
  relationTypes: string[] = [],
) {
  for (const type of relationTypes) {
    const foundRelation = entityRelations.find(
      relation => relation.type === type,
    );
    if (foundRelation) {
      return foundRelation; // Return the first found relation and stop
    }
  }
  return null;
}

const useStyles = makeStyles(theme => ({
  breadcrumbs: {
    color: theme.page.fontColor,
    fontSize: theme.typography.caption.fontSize,
    textTransform: 'uppercase',
    marginTop: theme.spacing(1),
    opacity: 0.8,
    '& span ': {
      color: theme.page.fontColor,
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
    },
  },
}));

/**
 * EntityLayout is a compound component, which allows you to define a layout for
 * entities using a sub-navigation mechanism.
 *
 * Consists of two parts: EntityLayout and EntityLayout.Route
 *
 * @example
 * ```jsx
 * <EntityLayout>
 *   <EntityLayout.Route path="/example" title="Example tab">
 *     <div>This is rendered under /example/anything-here route</div>
 *   </EntityLayout.Route>
 * </EntityLayout>
 * ```
 *
 * @public
 */
export const EntityLayout = (props: EntityLayoutProps) => {
  const {
    extraContextMenuItems,
    contextMenuOptions,
    children,
    NotFoundComponent,
    parentEntityRelations,
  } = props;
  const classes = useStyles();
  const { kind, namespace, name } = useRouteRefParams(entityRouteRef);
  const { entity, loading, error } = useAsyncEntity();
  const location = useLocation();
  const routes = useElementFilter(
    children,
    elements =>
      elements
        .selectByComponentData({
          key: dataKey,
          withStrictError:
            'Child of EntityLayout must be an EntityLayout.Route',
        })
        .getElements<EntityLayoutRouteProps>() // all nodes, element data, maintain structure or not?
        .flatMap(({ props: elementProps }) => {
          if (!entity || (elementProps.if && !elementProps.if(entity))) {
            return [];
          }

          return [
            {
              path: elementProps.path,
              title: elementProps.title,
              children: elementProps.children,
              tabProps: elementProps.tabProps,
            },
          ];
        }),
    [entity],
  );

  const { headerTitle, headerType } = headerProps(
    kind,
    namespace,
    name,
    entity,
  );

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const catalogRoute = useRouteRef(rootRouteRef);
  const unregisterRedirectRoute = useRouteRef(unregisterRedirectRouteRef);

  const cleanUpAfterRemoval = async () => {
    setConfirmationDialogOpen(false);
    navigate(
      unregisterRedirectRoute ? unregisterRedirectRoute() : catalogRoute(),
    );
  };

  const parentEntity = findParentRelation(
    entity?.relations ?? [],
    parentEntityRelations ?? [],
  );

  const catalogApi = useApi(catalogApiRef);
  const { value: ancestorEntity } = useAsync(async () => {
    if (parentEntity) {
      const entityRelations = (
        await catalogApi.getEntityByRef(parentEntity?.targetRef)
      )?.relations;
      return findParentRelation(entityRelations, parentEntityRelations);
    }
    return null;
  }, [parentEntity]);

  // Make sure to close the dialog if the user clicks links in it that navigate
  // to another entity.
  useEffect(() => {
    setConfirmationDialogOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const selectedInspectTab = searchParams.get('inspect');
  const showInspectTab = typeof selectedInspectTab === 'string';

  return (
    <Page
      themeId={
        entity?.spec?.type?.toString() ?? // NOSONAR - String will always resolve
        'home'
      }
    >
      <Header
        title={<EntityLayoutTitle title={headerTitle} entity={entity} />}
        pageTitleOverride={headerTitle}
        type={headerType}
        subtitle={
          parentEntity && (
            <Breadcrumbs separator=">" className={classes.breadcrumbs}>
              {ancestorEntity && (
                <EntityRefLink
                  entityRef={ancestorEntity.targetRef}
                  disableTooltip
                />
              )}
              <EntityRefLink
                entityRef={parentEntity.targetRef}
                disableTooltip
              />
              {name}
            </Breadcrumbs>
          )
        }
      >
        {entity && (
          <>
            <EntityLabels entity={entity} />
            <EntityContextMenu
              extraContextMenuItems={extraContextMenuItems}
              contextMenuOptions={contextMenuOptions}
              onUnregisterEntity={() => setConfirmationDialogOpen(true)}
              onInspectEntity={() => setSearchParams('inspect')}
            />
          </>
        )}
      </Header>
      {loading && <Progress />}
      {entity && <RoutedTabs routes={routes} />}
      {error && (
        <Content>
          <Alert severity="error">{error.toString()}</Alert>
        </Content>
      )}
      {!loading && !error && !entity && (
        <Content>
          {NotFoundComponent ?? (
            <WarningPanel title="Entity not found">
              There is no {kind} with the requested{' '}
              <Link to="https://backstage.io/docs/features/software-catalog/references">
                kind, namespace, and name
              </Link>
              .
            </WarningPanel>
          )}
        </Content>
      )}
      {showInspectTab && (
        <InspectEntityDialog
          entity={entity!}
          initialTab={
            (selectedInspectTab as ComponentProps<
              typeof InspectEntityDialog
            >['initialTab']) || undefined // NOSONAR - OSS Backstage code
          }
          onSelect={newTab => setSearchParams(`inspect=${newTab}`)}
          open
          onClose={() => setSearchParams()}
        />
      )}
      <UnregisterEntityDialog
        open={confirmationDialogOpen}
        entity={entity!}
        onConfirm={cleanUpAfterRemoval}
        onClose={() => setConfirmationDialogOpen(false)}
      />
    </Page>
  );
};

EntityLayout.Route = Route;
